<template>
  <div class="cart-item">
    <q-item class="q-pa-md bg-white" :class="isInvalid ? 'invalid' : ''">
      <q-item-section avatar top class="avatar justify-start q-pr-sm q-mr-xs">
        <div class="img-card">
          <q-img
            :src="
              $ossLimit(
                product.image ||
                  'https://static.bakmigm.co.id/menu/00110000/Default.jpg',
                64,
              )
            "
            :ratio="1"
            class="border-radius-8 img"
          />
          <div
            v-if="!product.inLive"
            class="out absolute-full flex flex-center text-weight-bolder font-size-12 text-center"
            style="line-height: 14px"
          >
            {{ $t('cart.goodsStatus.remove') }}
          </div>
          <div
            v-if="!product.inList && product.inLive"
            class="out absolute-full flex flex-center text-weight-bolder font-size-12 text-center"
            style="line-height: 14px"
          >
            {{ $t('cart.goodsStatus.error') }}
          </div>
          <div
            v-if="!product.inStock && product.inList && product.inLive"
            class="out absolute-full text-subtitle2 flex flex-center text-weight-bolder font-size-12"
          >
            {{ $t('cart.goodsStatus.noStock') }}
          </div>
        </div>
      </q-item-section>
      <q-item-section class="info" top :class="{ disabled: product.isSoldOut }">
        <q-item-label
          bold
          class="product-name font-size-14 break-word"
          :class="isInvalid ? 'gray-300' : ''"
        >
          {{ product.productName }}
        </q-item-label>
        <q-item-label v-if="product.modifiers?.length" caption>
          <div
            v-for="opti in product.modifiers"
            :key="opti.id"
            class="q-my-xxs break-word"
            :class="isInvalid ? 'gray-300' : ''"
          >
            {{ opti.name }}
          </div>
        </q-item-label>
        <q-item-label v-if="product.addons?.length" caption>
          <div
            v-for="addon in product.addons"
            :key="addon.externalProductId"
            class="q-my-xxs break-word"
            :class="isInvalid ? 'gray-300' : ''"
          >
            {{ addon.name }} x{{ addon.quantity }} {{ $pid(addon.totalPrice) }}
          </div>
        </q-item-label>

        <q-item-label
          v-if="product.comment"
          class="font-size-12 flex justify-between items-center q-mt-sm"
        >
          <span
            class="text-theme-gray"
            style="word-wrap: break-word; width: 100%; padding-bottom: 5px"
          >
            {{ product.comment }}
          </span>
        </q-item-label>
        <q-item-label v-if="delPrice" class="price-del">{{
          $pid(delPrice)
        }}</q-item-label>
        <q-item-label
          class="price text-weight-bolder font-size-16 flex items-center"
          :class="isInvalid ? 'gray-300' : ''"
        >
          <div break style="flex: 1">{{ $pid(price) }}</div>
          <MCounter
            v-model="quantity"
            class="counter"
            :step="STEP"
            :can-add="canAdd"
            :can-re="canReduce"
            :debounce-time="400"
            @change="onChange"
            @overflow="onOverflow"
          />
        </q-item-label>
        <q-item-label v-if="showStockError" class="stock-error">
          {{ $t('list.stockError', [product.productStock || 0]) }}
        </q-item-label>
      </q-item-section>
    </q-item>
    <slot name="pkg" :data="product" />
  </div>
</template>

<script setup>
import { ref, defineProps, computed, watchEffect, watch } from 'vue';
import { getCartDetailsData, getUpdateCartData } from 'src/api/cart.js';
import _ from 'lodash';
import { debounce, throttle } from 'quasar';
import { showDialog } from 'src/utils/error_config.js';
import { handleCheckoutError } from 'src/pages/checkout/utils';
import MCounter from 'src/components/MCounter/index.vue';
import { updateCart } from 'src/api/fullDineIn';
import { getCartTableId, getTable } from 'src/utils/common';
import { useLoading } from 'src/utils/hooks/loading';
import { use18n, useIns, useStore } from 'src/utils/v2_helper/common';
import { loadingMixin } from '../../mixin/loadingMixin';
import { useProductPrice } from '../../../utils/hooks/product/use-product-price';

const STEP = 1;
const app = useIns();
const { $t } = use18n();
const store = useStore();
const { loading, hide } = useLoading();

const props = defineProps({
  isInvalid: {
    type: Boolean,
    default: false,
  },
  product: {
    type: Object,
    default: () => ({ image: '' }),
  },
});
const _prod = computed(() => props.product);
const { promoList, showPromo } = useProductPrice(_prod);

const changeStep = ref(0);
const quantity = ref(props.product?.quantity || 0);

const canReduce = computed(() => quantity.value > 0);
const canAdd = computed(() => {
  const max =
    props.product?.productStock !== -1 ? props.product?.productStock : 9999;
  return quantity.value < max;
});

const price = computed(() => {
  const priceItem = _.last(promoList.value);
  if (showPromo.value) return priceItem.normalPrice;
  return priceItem.price;
});
const delPrice = computed(() => {
  const priceItem = _.last(promoList.value);
  if (showPromo.value) return null;
  if (price.value === priceItem.normalPrice) return null;
  return priceItem.normalPrice;
});
const showStockError = computed(() => {
  // -1表示没有库存限制
  if (props.product.productStock === -1) return false;
  return quantity.value > props.product.productStock;
});
const updateValue = (val) => {
  loading('UPDATE_CART');
  const params = { groupNumber: props.product.groupNumber };
  const callApi = app.isFullDineIn ? updateCart : getUpdateCartData;
  if (app.isFullDineIn) {
    params.tableNumber = getTable();
    params.quantity = changeStep.value;
    params.tableId = getCartTableId()?.tableId;
    params.cartId = getCartTableId()?.cartId;
  } else {
    params.quantity = val;
  }
  callApi(params)
    .then((cartResult) => {
      const { code, data, success } = cartResult;
      if (success) {
        changeStep.value = 0;
        store.dispatch('prefetch/getCartDetails', data);
      }
      if (handleCheckoutError(cartResult, app)) return;
      // TODO: 临时代码（有失效商品code不弹窗，其余都弹）
      if ([303, 307, 319, 517].indexOf(code) >= 0) showDialog(app, code);
    })
    .catch((e) => console.log(e))
    .finally(() => hide('UPDATE_CART'));
};
function onChange(val, step) {
  console.log('val step: ', val, step);
  if (app.isFullDineIn) changeStep.value = step;
  updateValue(val);
}
// 处理库存不够事件
function onOverflow() {
  app.$message.error($t('list.stockError', [app.product.productStock || 0]))
}
</script>

<style lang="scss" scoped>
.cart-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.break-word {
  word-break: break-word;
}
.invalid {
  .gray-300 {
    color: #d1d5db;
  }
}
.q-my-xxs {
  margin: 2px 0;
}
.cartBtn {
  :deep(.q-btn) {
    width: 24px !important;
    height: 24px !important;
    .q-icon {
      font-size: 18px !important;
    }
  }
}
.disabled {
  color: #d1d5db !important;
  :deep(input),
  .q-item__label--caption {
    color: #d1d5db !important;
  }
}
.img-card {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  .img {
    background-size: cover;
  }
  .out {
    position: absolute;
    left: 0;
    top: 0;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    text-align: center;
  }
  width: 64px;
  height: 64px;
}
// ==================

.info {
}
.product-name {
  margin-bottom: auto;
}
.price {
  margin-top: 0;
  flex-wrap: nowrap;
  .counter {
    margin-left: 16px;
    align-self: flex-end;
  }
}
.price-del {
  margin-top: 0;
  font-size: 12px;
  color: $grayFont;
  text-decoration: line-through;
}
.stock-error {
  margin-top: 10px;
  text-align: right;
  color: #C63527;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
}
</style>
